import { createTheme, type Theme } from '@mui/material/styles';
import { darkPalette, lightPalette, tokens } from './tokens';

const baseTheme = (mode: 'light' | 'dark'): Theme =>
  createTheme({
    palette: { mode, ...(mode === 'light' ? lightPalette : darkPalette) },
    shape: { borderRadius: tokens.radius.md },
    typography: {
      fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
      htmlFontSize: 16,
      fontSize: 14,
      h1: { fontSize: '2.25rem', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.15 },
      h2: { fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.015em', lineHeight: 1.2 },
      h3: { fontSize: '1.375rem', fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.3 },
      h4: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.4 },
      h5: { fontSize: '1rem', fontWeight: 600 },
      h6: { fontSize: '.875rem', fontWeight: 600 },
      body1: { fontSize: '.9375rem', lineHeight: 1.6 },
      body2: { fontSize: '.875rem', lineHeight: 1.5 },
      button: { fontWeight: 500, textTransform: 'none', letterSpacing: 0 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          'html, body, #root': { height: '100%' },
          body: { fontFeatureSettings: '"cv11", "ss01"' },
          '*::-webkit-scrollbar': { width: 10, height: 10 },
          '*::-webkit-scrollbar-thumb': {
            background: mode === 'light' ? '#CBD5E1' : '#334155',
            borderRadius: 8,
          },
          '*::-webkit-scrollbar-track': { background: 'transparent' },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true, variant: 'contained' },
        styleOverrides: {
          root: {
            borderRadius: tokens.radius.md,
            paddingInline: 16,
            minHeight: 36,
            transition: `all ${tokens.motion.base}`,
            '&:active': { transform: 'scale(0.98)' },
          },
          sizeSmall: { minHeight: 32 },
          sizeLarge: { minHeight: 44 },
        },
      },
      MuiTextField: {
        defaultProps: { fullWidth: true, size: 'small' },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radius.md,
            '& fieldset': { borderColor: mode === 'light' ? '#E2E8F0' : '#1F2937' },
            '&.Mui-focused fieldset': { borderWidth: 2 },
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: `1px solid ${mode === 'light' ? lightPalette.divider : darkPalette.divider}`,
          },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { borderRadius: tokens.radius.lg, boxShadow: tokens.shadow.sm },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { fontSize: 12, padding: '6px 10px', borderRadius: 6 },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 500, borderRadius: 8 },
        },
      },
      MuiTableCell: { styleOverrides: { root: { borderBottomColor: mode === 'light' ? '#F1F5F9' : '#1F2937' } } },
    },
  });

export const lightTheme = baseTheme('light');
export const darkTheme = baseTheme('dark');
